import { IItem } from "@Front/Components/DesignSystem/Menu/MenuItem";
import type {
	PermissionContextDto,
	RolePermissionsMatrixApiResponse,
} from "@Front/Api/LeCoffreApi/SuperAdmin/RolePermissions/RolePermissions";
import { resolveNotaryMenuOfficeIdentity } from "@Front/Utils/resolveNotaryMenuOfficeIdentity";
import type { UserOfficeAffiliation } from "le-coffre-resources/dist/Notary";
import { shouldHideGuestFolders } from "./officeItemsFilteringGuestFoldersHideHelpers";
import { checkIsSuperAdmin, checkIsOfficeAdmin } from "./useOfficeItemsFilterHelpers";

export interface OfficeItemsFilterContext {
	permissionContext: PermissionContextDto | null | undefined;
	loadingPermissions: boolean;
	/** Matrice déjà chargée : pendant un refresh, on conserve les candidats menu tant que contexte + matrice existent. */
	permissionsMatrix: RolePermissionsMatrixApiResponse | null;
	/** Office réel pour droits menu (sélection métier), pas le sentinel dossiers invités. */
	menuOfficeUid: string;
	menuRoleInOffice: string | null | undefined;
	/** UID brut store (peut être GUEST_FOLDERS) — politique masquage invité sans siège. */
	storeRawOfficeUid: string;
	officeItems: IItem[];
}

export interface OfficeItemsFilterResult {
	visibleItems: IItem[];
	shouldReturnEarly: boolean;
}

function returnWhenLoading(): OfficeItemsFilterResult {
	return { visibleItems: [], shouldReturnEarly: true };
}

function shouldBlankOfficeMenuWhilePermissionsLoad(context: OfficeItemsFilterContext): boolean {
	const { loadingPermissions, permissionContext, permissionsMatrix } = context;
	return loadingPermissions && (permissionContext == null || permissionsMatrix == null);
}

function returnWhenGuestFoldersHidden(
	storeRawOfficeUid: string,
	permissionContext: PermissionContextDto | null | undefined,
): OfficeItemsFilterResult | null {
	if (!shouldHideGuestFolders(storeRawOfficeUid, permissionContext)) {
		return null;
	}
	return { visibleItems: [], shouldReturnEarly: true };
}

function returnWhenSuperAdmin(
	permissionContext: PermissionContextDto | null | undefined,
	officeItems: IItem[],
): OfficeItemsFilterResult | null {
	if (!checkIsSuperAdmin(permissionContext)) {
		return null;
	}
	return { visibleItems: officeItems, shouldReturnEarly: true };
}

type OfficeAdminEarlyArgs = {
	permissionContext: PermissionContextDto | null | undefined;
	menuRoleInOffice: string | null | undefined;
	menuOfficeUid: string;
	officeItems: IItem[];
};

function returnWhenOfficeAdmin(args: OfficeAdminEarlyArgs): OfficeItemsFilterResult | null {
	const { permissionContext, menuRoleInOffice, menuOfficeUid, officeItems } = args;
	if (!checkIsOfficeAdmin(permissionContext, menuRoleInOffice, menuOfficeUid)) {
		return null;
	}
	return { visibleItems: officeItems, shouldReturnEarly: true };
}

function runOfficeItemsEarlyExitPhases(context: OfficeItemsFilterContext): OfficeItemsFilterResult | null {
	const { permissionContext, storeRawOfficeUid, menuOfficeUid, menuRoleInOffice, officeItems } = context;
	if (shouldBlankOfficeMenuWhilePermissionsLoad(context)) {
		return returnWhenLoading();
	}
	const guestHidden = returnWhenGuestFoldersHidden(storeRawOfficeUid, permissionContext);
	if (guestHidden) {
		return guestHidden;
	}
	const superAdmin = returnWhenSuperAdmin(permissionContext, officeItems);
	if (superAdmin) {
		return superAdmin;
	}
	const officeAdmin = returnWhenOfficeAdmin({
		permissionContext,
		menuRoleInOffice,
		menuOfficeUid,
		officeItems,
	});
	if (officeAdmin) {
		return officeAdmin;
	}
	return null;
}

/**
 * Candidats pour « Espace office » : garde-fous métier (invité, chargement initial sans matrice, rôles).
 * Pendant un refresh avec matrice + contexte déjà présents, ne pas vider le menu (aligné avec `useMatrixFilteredMenuItems`).
 * Le filtrage matrice (`can`) est appliqué dans `useMatrixFilteredMenuItems` avec `useOfficeMenuMatrixCan` (Menu header + burger).
 */
export function filterOfficeItemsByPermissions(context: OfficeItemsFilterContext): OfficeItemsFilterResult {
	const { officeItems } = context;
	const early = runOfficeItemsEarlyExitPhases(context);
	if (early) {
		return early;
	}
	return { visibleItems: officeItems, shouldReturnEarly: false };
}

export function resolveAndFilterOfficeMenuItems(args: {
	permissionContext: PermissionContextDto | null | undefined;
	loadingPermissions: boolean;
	permissionsMatrix: RolePermissionsMatrixApiResponse | null;
	activeOffice: UserOfficeAffiliation | null;
	availableOffices: UserOfficeAffiliation[];
	lastRealOfficeUidBeforeGuest: string | null;
	officeItems: IItem[];
}): IItem[] {
	const menuIdentity = resolveNotaryMenuOfficeIdentity({
		activeOffice: args.activeOffice,
		availableOffices: args.availableOffices,
		lastRealOfficeUidBeforeGuest: args.lastRealOfficeUidBeforeGuest,
		permissionContext: args.permissionContext,
	});
	const filterResult = filterOfficeItemsByPermissions({
		permissionContext: args.permissionContext,
		loadingPermissions: args.loadingPermissions,
		permissionsMatrix: args.permissionsMatrix,
		menuOfficeUid: menuIdentity.menuOfficeUid,
		menuRoleInOffice: menuIdentity.menuRoleInOffice,
		storeRawOfficeUid: menuIdentity.storeRawOfficeUid,
		officeItems: args.officeItems,
	});
	return filterResult.visibleItems;
}
